// Tampilan soal, pilihan jawaban, kunci, dan penilaian.
import { LET, esc } from '../core/format.js';

export const hintFor = q => q.type === 'pg' ? 'Pilih satu jawaban yang benar.' : q.type === 'bs' ? `Tentukan ${labelBS(q).join(' atau ')} untuk setiap pernyataan.` : 'Jawaban benar lebih dari satu. Pilih semua yang benar.';
const elCls = el => 'el-' + String(el).split(' ')[0];

const rapat = t => String(t ?? '').replace(/\s+/g, ' ').trim();

// Satu paragraf teks bacaan: escape, sorot kutipan bukti, larik puisi dengan <br>.
function paragrafHTML(p, sorot) {
  const baris = p.split('\n').map(rapat).filter(Boolean);
  const polos = baris.join(' '), patah = new Set();
  baris.reduce((n, x) => { patah.add(n + x.length); return n + x.length + 1; }, 0);
  const tanda = new Array(polos.length).fill(false);
  for (const k of sorot) {
    const i = polos.indexOf(rapat(k));
    if (i >= 0 && k) for (let j = i; j < i + rapat(k).length; j++) tanda[j] = true;
  }
  let h = '', buka = false;
  for (let j = 0; j < polos.length; j++) {
    if (tanda[j] !== buka) { h += buka ? '</mark>' : '<mark>'; buka = tanda[j]; }
    h += patah.has(j) && j < polos.length - 1 ? '<br>' : esc(polos[j]);
  }
  return h + (buka ? '</mark>' : '');
}

/** Ketuk gambar teks bacaan untuk melihatnya lebih besar (sekali pasang, berlaku di semua layar). */
export function pasangPerbesarGambar(doc = document) {
  const tutup = () => doc.querySelector('.lihat-gambar')?.remove();
  doc.addEventListener('click', e => {
    const t = e.target.closest?.('[data-perbesar]');
    if (!t) { if (e.target.closest?.('.lihat-gambar')) tutup(); return; }
    const img = t.querySelector('img');
    tutup();
    const el = doc.createElement('div');
    el.className = 'lihat-gambar';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Gambar diperbesar');
    el.innerHTML = `<button type="button" class="btn btn-sm lg-tutup">Tutup ✕</button><div class="lg-isi"><img src="${esc(img.getAttribute('src'))}" alt="${esc(img.alt)}"></div>`;
    doc.body.append(el);
    el.querySelector('.lg-tutup').focus();
  });
  doc.addEventListener('keydown', e => { if (e.key === 'Escape') tutup(); });
}

/**
 * Teks bacaan (soal kelompok).
 * @param {{judul: string, jenis: string, teks: string, bentuk?: string}} b
 * @param {{sorot?: string[], hanyaBukti?: boolean}} opsi hanyaBukti: tampilkan paragraf yang memuat bukti saja
 */
export function bacaanHTML(b, { sorot = [], hanyaBukti = false } = {}) {
  const puisi = b.bentuk === 'puisi', label = puisi ? 'Bait' : 'Paragraf';
  const par = String(b.teks || '').split(/\n\s*\n/);
  let nomor = 0;
  const isi = par.map(p => {
    if (/^#\s/.test(p)) return { html: `<h4 class="bc-sub">${esc(p.replace(/^#\s+/, ''))}</h4>`, sub: true };
    nomor++;
    const kena = sorot.filter(k => rapat(p).includes(rapat(k)));
    return { html: `<p class="bc-p" data-n="${nomor}"><span class="bc-n" aria-hidden="true">${nomor}</span>${paragrafHTML(p, kena)}</p>`, kena: kena.length, nomor };
  });
  if (hanyaBukti) {
    const dipakai = isi.filter(x => x.kena);
    return `<div class="bacaan bacaan-kutip${puisi ? ' puisi' : ''}">${dipakai.map(x => `<span class="bc-label">${label} ${x.nomor}</span>${x.html}`).join('')}</div>`;
  }
  // Gambar teks (infografis/diagram) diletakkan sesudah paragraf ke-`posisi` (0 = di atas teks).
  const g = b.gambar?.src ? b.gambar : null;
  const gambar = g ? `<figure class="bc-gambar"><button type="button" class="bc-perbesar" data-perbesar aria-label="Perbesar gambar: ${esc(g.alt)}"><img src="${esc(g.src)}" alt="${esc(g.alt)}" loading="lazy"></button><figcaption>Ketuk gambar untuk memperbesar</figcaption></figure>` : '';
  const ke = g ? isi.findIndex(x => x.nomor === g.posisi) : -1;
  const badan = isi.map((x, i) => x.html + (i === ke ? gambar : '')).join('');
  return `<article class="bacaan${puisi ? ' puisi' : ''}">${b.judul ? `<h3 class="bc-judul">${esc(b.judul)}</h3>` : ''}${g && ke < 0 && !g.posisi ? gambar : ''}${badan}${g && ke < 0 && g.posisi ? gambar : ''}${b.sumber ? `<p class="bc-sumber">Sumber: ${esc(b.sumber)}</p>` : ''}</article>`;
}

/**
 * HTML panel soal. Untuk soal kelompok, teks bacaan tampil di sini; pertanyaannya
 * ikut ditampilkan kecuali `tanya: false` (di layar kuis pertanyaan ada di panel jawaban).
 */
export function questionHTML(q, { tanya = true, sorot = [] } = {}) {
  const meta = `<div class="qmeta"><span class="pill ${elCls(q.el)}">${q.el}</span><span class="pill" style="color:var(--ink-soft)">${q.title}</span>${q.sumber === 'bank' ? '<span class="pill" style="color:var(--grape)">Soal pilihan guru</span>' : ''}</div>`;
  if (q.bacaan) return `${meta}${bacaanHTML(q.bacaan, { sorot })}${tanya ? `<div class="qtext q-tanya">${q.text}</div>` : ''}${q.vis ? `<div class="vis">${q.vis}</div>` : ''}`;
  return `${meta}<div class="qtext">${q.text}</div>${q.vis ? `<div class="vis">${q.vis}</div>` : ''}`;
}

// Label dua kategori soal Benar/Salah (bisa diganti, mis. "Sesuai"/"Tidak Sesuai").
export const labelBS = q => q.labels?.length === 2 ? q.labels : ['Benar', 'Salah'];
export const namaNilai = (q, v) => labelBS(q)[v ? 0 : 1];

export function answerHTML(q) {
  if (q.type === 'pg') return `<div class="opts">${q.options.map((o, i) => `<button class="opt" data-i="${i}" type="button"><span class="opt-l">${LET[i]}</span><span class="opt-t">${o}</span><span class="tag"></span></button>`).join('')}</div>`;
  if (q.type === 'bs') return `<div class="bs${labelBS(q).join('').length > 12 ? ' bs-panjang' : ''}">${q.statements.map((s, i) => `<div class="bs-row" data-r="${i}"><div class="bs-t"><b>${LET[i]}.</b> ${s.t}</div><div class="bs-btns"><button class="bs-b" data-r="${i}" data-v="1" type="button">${labelBS(q)[0]}</button><button class="bs-b" data-r="${i}" data-v="0" type="button">${labelBS(q)[1]}</button></div></div>`).join('')}</div>`;
  return `<div class="opts">${q.options.map((o, i) => `<button class="opt mc" data-i="${i}" aria-pressed="false" type="button"><span class="opt-box"></span><span class="opt-t">${o.t}</span><span class="tag"></span></button>`).join('')}</div>`;
}

export const selKosong = q => q.type === 'pg' ? null : q.type === 'bs' ? q.statements.map(() => null) : new Set();
export const selLengkap = (q, sel) => q.type === 'pg' ? sel != null : q.type === 'bs' ? sel.every(v => v != null) : sel.size > 0;

export function isRight(q, sel) {
  if (sel == null) return false;
  if (q.type === 'pg') return sel === q.answer;
  if (q.type === 'bs') return q.statements.every((s, i) => sel[i] === s.v);
  return q.options.every((o, i) => o.v === sel.has(i));
}

export function keyHTML(q) {
  if (q.type === 'pg') return `<b>${LET[q.answer]}.</b> ${q.options[q.answer]}`;
  if (q.type === 'bs') return `<ul>${q.statements.map((s, i) => `<li><b>${LET[i]}.</b> ${s.t} → <b>${namaNilai(q, s.v)}</b></li>`).join('')}</ul>`;
  return `<ul>${q.options.filter(o => o.v).map(o => `<li>${o.t}</li>`).join('')}</ul>`;
}

export function userHTML(q, sel) {
  if (sel == null) return '<i>Tidak dijawab</i>';
  if (q.type === 'pg') return `<b>${LET[sel]}.</b> ${q.options[sel]}`;
  if (q.type === 'bs') return sel.map((v, i) => `${LET[i]}: ${v == null ? '–' : namaNilai(q, v)}`).join(' · ');
  const arr = [...sel];
  return arr.length ? arr.map(i => q.options[i].t).join('; ') : '<i>Tidak dijawab</i>';
}

// Pilihan yang sudah ditandai benar/salah (untuk pembahasan).
export function markedHTML(q, sel) {
  if (q.type === 'pg') return `<div class="opts">${q.options.map((o, i) => { const c = i === q.answer ? 'ok' : i === sel ? 'no' : ''; return `<div class="opt ${c}" style="cursor:default"><span class="opt-l">${LET[i]}</span><span class="opt-t">${o}</span><span class="tag">${i === q.answer ? 'Jawaban benar' : i === sel ? 'Pilihanmu' : ''}</span></div>`; }).join('')}</div>`;
  if (q.type === 'bs') return `<div class="bs">${q.statements.map((s, i) => { const u = sel ? sel[i] : null; return `<div class="bs-row ${u === s.v ? 'ok' : 'no'}"><div class="bs-t"><b>${LET[i]}.</b> ${s.t}</div><div class="bs-key">Jawabanmu: ${u == null ? '–' : namaNilai(q, u)} · Kunci: ${namaNilai(q, s.v).toUpperCase()}</div></div>`; }).join('')}</div>`;
  const ss = sel || new Set();
  return `<div class="opts">${q.options.map((o, i) => { const s = ss.has(i), v = o.v, c = v && s ? 'ok' : v ? 'miss' : s ? 'no' : ''; return `<div class="opt ${c}" style="cursor:default"><span class="opt-box">${s ? '✓' : ''}</span><span class="opt-t">${o.t}</span><span class="tag">${v && s ? 'Benar ✓' : v ? 'Harusnya dipilih' : s ? 'Bukan jawaban' : ''}</span></div>`; }).join('')}</div>`;
}
